'use client';

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'es-toolkit';
import { isEqual } from 'es-toolkit/compat';
import { Box, Flex, Text, VStack, HStack, Slider, Accordion, Heading, LucideIcon } from '@skin-seoul/design-system';
import CustomAccordionIndicator from '@/components/ui/CustomAccordionIndicator';
import { type FilterOptions, type FilterSummary } from '../types/filters';
import { useRegionCurrency, formatCurrency, CLASSNAME } from '@skin-seoul/service';
import FilterSection from './FilterSection';
import SubcategoryItem from './SubcategoryItem';
import { useFilterHandlers } from '../hooks/useFilterHandlers';
import { buildFiltersFromState, hasMeaningfulFilters, getClearedFilterState, type FilterState } from '../utils/collectionFilterBuilders';
import { useDynamicCategories, findTermIdBySlug, findSlugByTermId, generateCategoryUrl, findMainCategoryForSubcategory, findMainCategoryForSubSubcategory, getSubSubcategoriesForSubcategory, findParentSubcategoryForSubSubcategory, type TaxonomyCategory } from '../utils/categoryUtils';
import SubSubcategoriesSection from './SubSubcategoriesSection';
import { findBrandTermIdFromSummary } from '../utils/brandUtils';
import { useSafeRouter } from '@/hooks/useSafeRouter';
import { useSearchParams } from 'next/navigation';
import { buildFilterUrlString, parseFilterUrlString, updateFilterUrl, type FilterUrlState } from '../utils/filterUrlUtils';
import { useDidUpdate } from '@skin-seoul/utils';
interface FilterSidebarProps {
  onFilterChange?: (filters: FilterOptions) => void;
  onSortByChange?: (sortBy: string) => void;
  category?: string;
  subcategory?: string;
  subSubcategory?: string;
  brand?: string;
  filterSummary?: FilterSummary;
  sortBy?: string;
  productCount?: number;
  hideCategoryFilter?: boolean;
  hideAllFilters?: boolean;
  categories?: TaxonomyCategory[];
}
const FilterSidebar = ({
  onFilterChange,
  onSortByChange,
  category,
  subcategory,
  subSubcategory,
  brand,
  filterSummary,
  sortBy,
  productCount = 0,
  hideCategoryFilter = false,
  hideAllFilters = false,
  categories: categoriesProp
}: FilterSidebarProps) => {
  const router = useSafeRouter();
  const searchParams = useSearchParams();
  const {
    currency
  } = useRegionCurrency();
  const initialFilterState = useMemo(() => {
    const filtersParam = searchParams.get('filters');
    if (!filtersParam) {
      return {
        minPrice: undefined,
        maxPriceSelected: undefined,
        selectedBrands: [],
        selectedSkinTypes: [],
        selectedSkinConcerns: [],
        selectedIngredients: []
      };
    }
    const parsed = parseFilterUrlString(filtersParam);
    return {
      minPrice: parsed.minPrice,
      maxPriceSelected: parsed.maxPrice,
      selectedBrands: parsed.brands,
      selectedSkinTypes: parsed.skinTypes,
      selectedSkinConcerns: parsed.skinConcerns,
      selectedIngredients: parsed.ingredients
    };
  }, [searchParams]);
  const [minPrice, setMinPrice] = useState<number | undefined>(initialFilterState.minPrice);
  const [maxPriceSelected, setMaxPriceSelected] = useState<number | undefined>(initialFilterState.maxPriceSelected);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | undefined>();
  const [selectedSubSubcategory, setSelectedSubSubcategory] = useState<number | undefined>();
  const [selectedBrands, setSelectedBrands] = useState<number[]>(initialFilterState.selectedBrands);
  const [selectedSkinTypes, setSelectedSkinTypes] = useState<number[]>(initialFilterState.selectedSkinTypes);
  const [selectedSkinConcerns, setSelectedSkinConcerns] = useState<number[]>(initialFilterState.selectedSkinConcerns);
  const [selectedIngredients, setSelectedIngredients] = useState<number[]>(initialFilterState.selectedIngredients);
  const [brandSearchQuery, setBrandSearchQuery] = useState<string>('');
  const [userExpandedCategory, setUserExpandedCategory] = useState<number | null>(null);
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  // Temporary state for slider values during dragging
  const [tempPriceRange, setTempPriceRange] = useState<[number, number] | null>(null);
  const isDraggingRef = useRef(false);
  const currentFilterState: FilterState = useMemo(() => ({
    minPrice,
    maxPrice: maxPriceSelected,
    selectedCategories,
    selectedSubcategory,
    selectedSubSubcategory,
    selectedBrands,
    selectedSkinTypes,
    selectedSkinConcerns,
    selectedIngredients
  }), [minPrice, maxPriceSelected, selectedCategories, selectedSubcategory, selectedSubSubcategory, selectedBrands, selectedSkinTypes, selectedSkinConcerns, selectedIngredients]);
  const {
    categories: categoriesFromHook
  } = useDynamicCategories();
  const mainCategories = categoriesProp ?? categoriesFromHook;
  const isUncategorizedCategory = category === 'uncategorized';
  const routeBasedSubcategory = useMemo((): number | null => {
    if (subcategory) {
      return findTermIdBySlug(mainCategories, subcategory) || null;
    }
    return null;
  }, [subcategory, mainCategories]);
  const routeBasedSubSubcategory = useMemo((): number | null => {
    if (subSubcategory) {
      return findTermIdBySlug(mainCategories, subSubcategory) || null;
    }
    return null;
  }, [subSubcategory, mainCategories]);
  const activeMainCategory = useMemo((): number | null => {
    if (subSubcategory) {
      const subSubcategoryTermId = findTermIdBySlug(mainCategories, subSubcategory);
      if (subSubcategoryTermId) {
        return findMainCategoryForSubSubcategory(mainCategories, subSubcategoryTermId);
      }
    }
    if (subcategory) {
      const subcategoryTermId = findTermIdBySlug(mainCategories, subcategory);
      if (subcategoryTermId) {
        return findMainCategoryForSubcategory(mainCategories, subcategoryTermId);
      }
    }
    if (category) {
      return findTermIdBySlug(mainCategories, category) || null;
    }
    return null;
  }, [category, subcategory, subSubcategory, mainCategories]);
  const shouldShowSubSubcategories = useMemo(() => {
    if (subcategory && !subSubcategory) {
      const subcategoryTermId = findTermIdBySlug(mainCategories, subcategory);
      if (!subcategoryTermId) {
        return false;
      }
      const subSubcategories = getSubSubcategoriesForSubcategory(mainCategories, subcategoryTermId);
      return subSubcategories.length > 0;
    }
    if (subSubcategory) {
      const subSubcategoryTermId = findTermIdBySlug(mainCategories, subSubcategory);
      if (!subSubcategoryTermId) {
        return false;
      }
      const parentSubcategory = findParentSubcategoryForSubSubcategory(mainCategories, subSubcategoryTermId);
      return parentSubcategory?.subCategories && parentSubcategory.subCategories.length > 0;
    }
    return false;
  }, [subcategory, subSubcategory, mainCategories]);
  const availableAccordionSections = useMemo(() => {
    const shouldShowCategories = !hideCategoryFilter && !shouldShowSubSubcategories && !subcategory;
    const ALWAYS_VISIBLE_SECTIONS = shouldShowCategories ? ['categories'] : [];
    if (productCount <= 0) {
      return ALWAYS_VISIBLE_SECTIONS;
    }
    const CORE_SECTIONS = shouldShowCategories ? ['categories', 'price'] : ['price'];
    const OPTIONAL_SECTIONS = [{
      key: 'brands',
      hasData: Boolean(filterSummary?.brand?.length)
    }, {
      key: 'skintypes',
      hasData: Boolean(filterSummary?.skinType?.length)
    }, {
      key: 'skinconcerns',
      hasData: Boolean(filterSummary?.skinConcern?.length)
    }, {
      key: 'ingredients',
      hasData: Boolean(filterSummary?.ingredient?.length)
    }];
    const availableOptionalSections = OPTIONAL_SECTIONS.filter(section => section.hasData).map(section => section.key);
    return [...CORE_SECTIONS, ...availableOptionalSections];
  }, [filterSummary, productCount, hideCategoryFilter, shouldShowSubSubcategories, subcategory]);
  const areAllExpanded = useMemo(() => {
    if (!filterSummary) {
      return false;
    }
    return availableAccordionSections.every(section => accordionValue.includes(section));
  }, [availableAccordionSections, accordionValue, filterSummary]);
  function getDefaultExpandedSections(hideCategoryFilter = false, shouldShowSubSubcategories = false, hasSubcategory = false) {
    if (hideCategoryFilter || shouldShowSubSubcategories || hasSubcategory) {
      return ['price'];
    }
    return ['categories', 'price'];
  }
  useEffect(() => {
    setAccordionValue(getDefaultExpandedSections(hideCategoryFilter, shouldShowSubSubcategories, Boolean(subcategory)));
  }, [hideCategoryFilter, shouldShowSubSubcategories, subcategory]);
  const handleExpandCollapseAll = () => {
    const DEFAULT_EXPANDED_SECTIONS = getDefaultExpandedSections(hideCategoryFilter, shouldShowSubSubcategories, Boolean(subcategory));
    if (areAllExpanded) {
      setAccordionValue(DEFAULT_EXPANDED_SECTIONS);
    } else {
      setAccordionValue([...availableAccordionSections]);
    }
  };
  useEffect(() => {
    if (!mainCategories || mainCategories.length === 0) {
      return;
    }
    setSelectedCategories([]);
    setSelectedSubcategory(undefined);
    if (subcategory) {
      const subcategoryTermId = findTermIdBySlug(mainCategories, subcategory);
      if (subcategoryTermId) {
        setSelectedSubcategory(subcategoryTermId);
        return;
      }
    }
    if (category) {
      const categoryTermId = findTermIdBySlug(mainCategories, category);
      if (categoryTermId) {
        setSelectedCategories([categoryTermId]);
        return;
      }
    }
  }, [category, subcategory, mainCategories]);
  useEffect(() => {
    const filtersParam = searchParams.get('filters');
    const hasUrlFilters = Boolean(filtersParam);
    if (brand && filterSummary && !hasUrlFilters) {
      const brandTermId = findBrandTermIdFromSummary(brand, filterSummary);
      if (brandTermId) {
        setSelectedCategories([]);
        setSelectedSubcategory(undefined);
        setSelectedBrands([brandTermId]);
      }
    }
  }, [brand, filterSummary, searchParams]);
  const onSortByChangeRef = useRef(onSortByChange);
  onSortByChangeRef.current = onSortByChange;
  const prevFiltersParamRef = useRef<string | null>(null);
  const resetFilterParams = useCallback(() => {
    setMinPrice(undefined);
    setMaxPriceSelected(undefined);
    setSelectedBrands([]);
    setSelectedSkinTypes([]);
    setSelectedSkinConcerns([]);
    setSelectedIngredients([]);
  }, []);
  useEffect(() => {
    const filtersParam = searchParams.get('filters');
    if (prevFiltersParamRef.current === filtersParam) {
      return;
    }
    if (prevFiltersParamRef.current === null) {
      prevFiltersParamRef.current = filtersParam;
      return;
    }
    prevFiltersParamRef.current = filtersParam;
    if (!filtersParam) {
      resetFilterParams();
      return;
    }
    const parsed = parseFilterUrlString(filtersParam);
    setMinPrice(parsed.minPrice);
    setMaxPriceSelected(parsed.maxPrice);
    setSelectedBrands(parsed.brands);
    setSelectedSkinTypes(parsed.skinTypes);
    setSelectedSkinConcerns(parsed.skinConcerns);
    setSelectedIngredients(parsed.ingredients);
    if (parsed.sortBy && onSortByChangeRef.current) {
      onSortByChangeRef.current(parsed.sortBy);
    }
  }, [searchParams, resetFilterParams]);
  const syncUrlWithFilters = useCallback((newState: Partial<{
    brands: number[];
    skinTypes: number[];
    skinConcerns: number[];
    ingredients: number[];
    minPrice: number | undefined;
    maxPrice: number | undefined;
    sortBy: string | undefined;
  }>) => {
    const effectiveMinPrice = 'minPrice' in newState ? newState.minPrice : minPrice;
    const effectiveMaxPrice = 'maxPrice' in newState ? newState.maxPrice : maxPriceSelected;
    const urlState: FilterUrlState = {
      brands: newState.brands ?? selectedBrands,
      skinTypes: newState.skinTypes ?? selectedSkinTypes,
      skinConcerns: newState.skinConcerns ?? selectedSkinConcerns,
      ingredients: newState.ingredients ?? selectedIngredients,
      minPrice: effectiveMinPrice,
      maxPrice: effectiveMaxPrice,
      sortBy: newState.sortBy ?? sortBy
    };
    const filterString = buildFilterUrlString(urlState);
    updateFilterUrl(filterString);
  }, [selectedBrands, selectedSkinTypes, selectedSkinConcerns, selectedIngredients, minPrice, maxPriceSelected, sortBy]);
  const pendingPriceUpdateRef = useRef<{
    minPrice: number | undefined;
    maxPrice: number | undefined;
  } | null>(null);
  const fallbackPriceUpdate = useMemo(() => {
    return debounce(() => {
      if (pendingPriceUpdateRef.current && isDraggingRef.current) {
        const {
          minPrice: newMinPrice,
          maxPrice: newMaxPrice
        } = pendingPriceUpdateRef.current;
        setMinPrice(newMinPrice);
        setMaxPriceSelected(newMaxPrice);
        syncUrlWithFilters({
          minPrice: newMinPrice,
          maxPrice: newMaxPrice
        });
        pendingPriceUpdateRef.current = null;
        isDraggingRef.current = false;
        setTempPriceRange(null);
      }
    }, 1000);
  }, [syncUrlWithFilters]);
  useEffect(() => {
    return () => {
      fallbackPriceUpdate.cancel();
    };
  }, [fallbackPriceUpdate]);
  const priceRange = useMemo(() => {
    const apiPriceRange = filterSummary?.priceRange;
    if (!apiPriceRange) {
      return [0, 0];
    }
    if (tempPriceRange && isDraggingRef.current) {
      return tempPriceRange;
    }
    return [minPrice || apiPriceRange.min, maxPriceSelected || apiPriceRange.max];
  }, [minPrice, maxPriceSelected, filterSummary?.priceRange, tempPriceRange]);
  const handlePriceRangeChange = useCallback((value: number[]) => {
    const apiPriceRange = filterSummary?.priceRange;
    if (!apiPriceRange) {
      return;
    }
    const newMinPrice = value[0] !== apiPriceRange.min ? value[0] : undefined;
    const newMaxPrice = value[1] !== apiPriceRange.max ? value[1] : undefined;
    setTempPriceRange([value[0], value[1]]);
    isDraggingRef.current = true;
    pendingPriceUpdateRef.current = {
      minPrice: newMinPrice,
      maxPrice: newMaxPrice
    };
    fallbackPriceUpdate();
  }, [filterSummary?.priceRange, fallbackPriceUpdate]);
  const handlePriceRangeChangeEnd = useCallback(() => {
    isDraggingRef.current = false;
    setTempPriceRange(null);
    fallbackPriceUpdate.cancel();
    if (pendingPriceUpdateRef.current) {
      const {
        minPrice: newMinPrice,
        maxPrice: newMaxPrice
      } = pendingPriceUpdateRef.current;
      setMinPrice(newMinPrice);
      setMaxPriceSelected(newMaxPrice);
      syncUrlWithFilters({
        minPrice: newMinPrice,
        maxPrice: newMaxPrice
      });
      pendingPriceUpdateRef.current = null;
    }
  }, [syncUrlWithFilters, fallbackPriceUpdate]);
  const filteredBrands = useMemo(() => {
    if (!filterSummary?.brand) {
      return [];
    }
    if (!brandSearchQuery.trim()) {
      return filterSummary.brand;
    }
    return filterSummary.brand.filter(brand => brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase()));
  }, [filterSummary?.brand, brandSearchQuery]);
  useEffect(() => {
    const resetSelections = () => {
      setSelectedSubcategory(undefined);
      setSelectedSubSubcategory(undefined);
      setSelectedCategories([]);
    };
    const selectSubcategory = (subcategoryTermId: number) => {
      setSelectedSubcategory(subcategoryTermId);
      setSelectedSubSubcategory(undefined);
      setSelectedCategories([]);
    };
    const selectSubSubcategory = (subSubcategoryTermId: number) => {
      setSelectedSubSubcategory(subSubcategoryTermId);
      setSelectedSubcategory(undefined);
      setSelectedCategories([]);
    };
    if (subSubcategory) {
      const subSubcategoryTermId = findTermIdBySlug(mainCategories, subSubcategory);
      if (subSubcategoryTermId && subSubcategoryTermId !== selectedSubSubcategory) {
        selectSubSubcategory(subSubcategoryTermId);
      }
      return;
    }
    if (subcategory) {
      const subcategoryTermId = findTermIdBySlug(mainCategories, subcategory);
      if (subcategoryTermId && subcategoryTermId !== selectedSubcategory) {
        selectSubcategory(subcategoryTermId);
      }
      return;
    }
    resetSelections();
  }, [category, subcategory, subSubcategory, mainCategories, selectedSubcategory, selectedSubSubcategory]);
  const cachedFiltersRef = useRef<FilterOptions>({});
  const isFirstFilterUpdateRef = useRef(true);
  useDidUpdate(() => {
    const newFilters = buildFiltersFromState(currentFilterState);
    const filtersChanged = !isEqual(newFilters, cachedFiltersRef.current);
    if (filtersChanged) {
      cachedFiltersRef.current = newFilters;
      if (isFirstFilterUpdateRef.current) {
        isFirstFilterUpdateRef.current = false;
        return;
      }
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
    }
  }, [currentFilterState]);
  const handleClearFilter = () => {
    const clearedState = getClearedFilterState();
    setMinPrice(clearedState.minPrice);
    setMaxPriceSelected(clearedState.maxPrice);
    setSelectedCategories(clearedState.selectedCategories || []);
    setSelectedSubcategory(clearedState.selectedSubcategory);
    setSelectedSubSubcategory(clearedState.selectedSubSubcategory);
    setSelectedBrands(clearedState.selectedBrands || []);
    setSelectedSkinTypes(clearedState.selectedSkinTypes || []);
    setSelectedSkinConcerns(clearedState.selectedSkinConcerns || []);
    setSelectedIngredients(clearedState.selectedIngredients || []);
    setBrandSearchQuery('');
    updateFilterUrl('');
    if (onFilterChange) {
      onFilterChange({});
    }
  };
  const {
    handleBrandChange,
    handleSkinTypeChange,
    handleSkinConcernChange,
    handleIngredientChange
  } = useFilterHandlers({
    selectedBrands,
    selectedSkinTypes,
    selectedSkinConcerns,
    selectedIngredients,
    setSelectedBrands,
    setSelectedSkinTypes,
    setSelectedSkinConcerns,
    setSelectedIngredients,
    syncUrlWithFilters
  });
  const handleCategoryNavigate = (categoryTermId: number) => {
    const categorySlug = findSlugByTermId(mainCategories, categoryTermId);
    if (categorySlug) {
      const urlState: FilterUrlState = {
        brands: selectedBrands,
        skinTypes: selectedSkinTypes,
        skinConcerns: selectedSkinConcerns,
        ingredients: selectedIngredients,
        minPrice,
        maxPrice: maxPriceSelected,
        sortBy
      };
      const filterString = buildFilterUrlString(urlState);
      const newUrl = filterString ? `/collections/${categorySlug}/?filters=${filterString}` : `/collections/${categorySlug}/`;
      router.push(newUrl);
    }
  };
  const handleCategoryToggle = (categoryTermId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setUserExpandedCategory(userExpandedCategory === categoryTermId ? null : categoryTermId);
  };
  const handleSubcategoryClick = (subcategoryTermId: number, _subcategoryName: string) => {
    const categoryUrl = generateCategoryUrl(mainCategories, subcategoryTermId);
    if (categoryUrl !== '/collections/') {
      const urlState: FilterUrlState = {
        brands: selectedBrands,
        skinTypes: selectedSkinTypes,
        skinConcerns: selectedSkinConcerns,
        ingredients: selectedIngredients,
        minPrice,
        maxPrice: maxPriceSelected,
        sortBy
      };
      const filterString = buildFilterUrlString(urlState);
      const newUrl = filterString ? `${categoryUrl}?filters=${filterString}` : categoryUrl;
      router.push(newUrl);
      return;
    }
    const isCurrentlySelected = selectedSubcategory === subcategoryTermId;
    if (!isCurrentlySelected) {
      setSelectedSubcategory(subcategoryTermId);
      setSelectedCategories([]);
      setSelectedSubSubcategory(undefined);
    }
  };
  const handleSubSubcategoryClick = (subSubcategoryTermId: number, _subSubcategoryName: string) => {
    const categoryUrl = generateCategoryUrl(mainCategories, subSubcategoryTermId);
    if (categoryUrl !== '/collections/') {
      const urlState: FilterUrlState = {
        brands: selectedBrands,
        skinTypes: selectedSkinTypes,
        skinConcerns: selectedSkinConcerns,
        ingredients: selectedIngredients,
        minPrice,
        maxPrice: maxPriceSelected,
        sortBy
      };
      const filterString = buildFilterUrlString(urlState);
      const newUrl = filterString ? `${categoryUrl}?filters=${filterString}` : categoryUrl;
      router.push(newUrl);
      return;
    }
    const isCurrentlySelected = selectedSubSubcategory === subSubcategoryTermId;
    if (!isCurrentlySelected) {
      setSelectedSubSubcategory(subSubcategoryTermId);
      setSelectedCategories([]);
      setSelectedSubcategory(undefined);
    }
  };
  return <Box width={{
    base: '100%',
    tablet: '200px',
    desktop: '248px'
  }} height="fit-content" data-sentry-element="Box" data-sentry-component="FilterSidebar" data-sentry-source-file="FilterSidebar.tsx">
      <Box display="flex" flexDirection="column" gap={4} paddingBottom={2} borderBottom="1px solid" borderColor="neutral.100" data-sentry-element="Box" data-sentry-source-file="FilterSidebar.tsx">
        <Box gap={2} display={{
        base: 'none',
        tablet: 'flex'
      }} alignItems="center" data-sentry-element="Box" data-sentry-source-file="FilterSidebar.tsx">
          <Heading as="h6" textStyle="heading.h6" fontWeight="medium" color="neutral.800" data-sentry-element="Heading" data-sentry-source-file="FilterSidebar.tsx">
            Filters
          </Heading>
        </Box>
        <Flex justify="space-between" data-sentry-element="Flex" data-sentry-source-file="FilterSidebar.tsx">
          <Text fontWeight="regular" textStyle="paragraph.medium" color="primary.600" cursor="pointer" onClick={handleExpandCollapseAll} _hover={{
          textDecoration: 'underline'
        }} data-sentry-element="Text" data-sentry-source-file="FilterSidebar.tsx">
            {areAllExpanded ? 'Collapse all' : 'Expand all'}
          </Text>
          {hasMeaningfulFilters(currentFilterState) && <Text fontWeight="regular" textStyle="paragraph.medium" color="primary.600" cursor="pointer" onClick={handleClearFilter} _hover={{
          textDecoration: 'underline'
        }}>
              Clear filter
            </Text>}
        </Flex>
      </Box>

      <Accordion.Root multiple value={accordionValue} onValueChange={details => setAccordionValue(details.value)} data-sentry-element="Accordion.Root" data-sentry-source-file="FilterSidebar.tsx">
        {shouldShowSubSubcategories && <SubSubcategoriesSection subcategory={subcategory} subSubcategory={subSubcategory} />}

        {!hideCategoryFilter && !shouldShowSubSubcategories && !subcategory && <Accordion.Item value="categories" borderBottom="1px solid" borderColor="neutral.100">
            <Accordion.ItemTrigger justifyContent="space-between" cursor="pointer" paddingY={6}>
              <Text fontWeight="regular" flex="1" textAlign="left" textStyle="Label.large" color="neutral.600">
                Product Category
              </Text>
              <CustomAccordionIndicator value="categories" accordionValue={accordionValue} />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent paddingBottom={4}>
              <VStack align="stretch" gap={3}>
                {mainCategories.map((category: TaxonomyCategory) => {
              const isCategoryExpanded = activeMainCategory === category.termId || userExpandedCategory === category.termId;
              const hasSubCategories = category.subCategories && category.subCategories.length > 0;
              const shouldShowSubCategories = isCategoryExpanded && hasSubCategories;
              return <Box key={category.termId}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                        <Text fontWeight="regular" textStyle="paragraph.medium" color={isCategoryExpanded ? 'primary.500' : 'neutral.600'} cursor="pointer" onClick={() => handleCategoryNavigate(category.termId)} _hover={{
                    color: 'primary.500'
                  }} transition="all 0.2s" className={CLASSNAME.NOT_TRANSLATE}>
                          {category.name}
                        </Text>
                        <Box cursor="pointer" onClick={e => handleCategoryToggle(category.termId, e)} padding={1} _hover={{
                    color: 'primary.500'
                  }} transition="all 0.2s">
                          <LucideIcon name="ChevronRight" style={{
                      transform: isCategoryExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }} boxSize={5} strokeWidth={1} />
                        </Box>
                      </Box>

                      {shouldShowSubCategories && <VStack align="stretch" gap={1} marginLeft={1} marginTop={2}>
                          {category.subCategories?.map((subCat: TaxonomyCategory) => {
                    const isSubcategoryActive = routeBasedSubcategory === subCat.termId || selectedSubcategory === subCat.termId;
                    return <SubcategoryItem key={subCat.termId} subcategory={subCat} isActive={isSubcategoryActive} routeBasedSubSubcategory={routeBasedSubSubcategory} selectedSubSubcategory={selectedSubSubcategory} onSubcategoryClick={handleSubcategoryClick} onSubSubcategoryClick={handleSubSubcategoryClick} />;
                  })}
                        </VStack>}
                    </Box>;
            })}
              </VStack>
            </Accordion.ItemContent>
          </Accordion.Item>}

        {!hideAllFilters && <>
            {filterSummary?.priceRange && filterSummary.priceRange.max > 0 && productCount > 0 ? <Accordion.Item value="price" borderBottom="1px solid" borderColor="neutral.100">
                <Accordion.ItemTrigger justifyContent="space-between" cursor="pointer" paddingY={6}>
                  <Text fontWeight="regular" flex="1" textAlign="left" textStyle="paragraph.medium" color="neutral.600">
                    Price
                  </Text>
                  <CustomAccordionIndicator value="price" accordionValue={accordionValue} />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent paddingBottom={4}>
                  <VStack align="stretch" gap={2}>
                    <Slider.Root value={priceRange} onValueChange={details => handlePriceRangeChange(details.value)} onValueChangeEnd={handlePriceRangeChangeEnd} min={filterSummary.priceRange.min} max={filterSummary.priceRange.max} step={5} cursor="pointer">
                      <Slider.Control>
                        <Slider.Track backgroundColor="neutral.200" height="4px">
                          <Slider.Range backgroundColor="neutral.800" />
                        </Slider.Track>
                        <Slider.Thumb index={0} backgroundColor="neutral.800" border="none" width="16px" height="16px" borderRadius="50%">
                          <Slider.HiddenInput />
                        </Slider.Thumb>
                        <Slider.Thumb index={1} backgroundColor="neutral.800" border="none" width="16px" height="16px" borderRadius="50%">
                          <Slider.HiddenInput />
                        </Slider.Thumb>
                      </Slider.Control>
                    </Slider.Root>
                    <HStack justify="space-between">
                      <Text textStyle="paragraph.small" color="neutral.600">
                        Min
                      </Text>
                      <Text textStyle="paragraph.small" color="neutral.600">
                        Max
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text textStyle="paragraph.small" paddingX={2} paddingY={0.5} backgroundColor="primary.50" color="primary.600" borderRadius="4px">
                        {formatCurrency(currency, priceRange[0])}
                      </Text>
                      <Text textStyle="paragraph.small" paddingX={2} paddingY={0.5} backgroundColor="primary.50" color="primary.600">
                        {formatCurrency(currency, priceRange[1])}
                      </Text>
                    </HStack>
                  </VStack>
                </Accordion.ItemContent>
              </Accordion.Item> : productCount > 0 ? <Accordion.Item value="price" borderBottom="1px solid" borderColor="neutral.100">
                <Accordion.ItemTrigger justifyContent="space-between" cursor="pointer" paddingY={6}>
                  <Text fontWeight="regular" flex="1" textAlign="left" textStyle="paragraph.medium" color="neutral.600">
                    Price
                  </Text>
                  <CustomAccordionIndicator value="price" accordionValue={accordionValue} />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent paddingBottom={4}>
                  <VStack align="stretch" gap={2}>
                    <Box height="20px" backgroundColor="neutral.100" borderRadius="4px" />
                    <HStack justify="space-between">
                      <Box width="40px" height="16px" backgroundColor="neutral.100" borderRadius="4px" />
                      <Box width="40px" height="16px" backgroundColor="neutral.100" borderRadius="4px" />
                    </HStack>
                    <HStack justify="space-between">
                      <Box width="60px" height="24px" backgroundColor="neutral.100" borderRadius="4px" />
                      <Box width="60px" height="24px" backgroundColor="neutral.100" borderRadius="4px" />
                    </HStack>
                  </VStack>
                </Accordion.ItemContent>
              </Accordion.Item> : null}

            {!brand && productCount > 0 && <FilterSection value="brands" title="Brand" items={filteredBrands} selectedItems={selectedBrands} onItemChange={handleBrandChange} showSearch searchQuery={brandSearchQuery} onSearchChange={setBrandSearchQuery} searchPlaceholder="Search here" skeletonCount={6} accordionValue={accordionValue} isLoading={!filterSummary} />}

            {!isUncategorizedCategory && productCount > 0 && <FilterSection value="skintypes" title="Skin Type" items={filterSummary?.skinType} selectedItems={selectedSkinTypes} onItemChange={handleSkinTypeChange} skeletonCount={4} accordionValue={accordionValue} isLoading={!filterSummary} />}

            {!isUncategorizedCategory && productCount > 0 && <FilterSection value="skinconcerns" title="Skin Concern" items={filterSummary?.skinConcern} selectedItems={selectedSkinConcerns} onItemChange={handleSkinConcernChange} skeletonCount={5} accordionValue={accordionValue} isLoading={!filterSummary} />}

            {!isUncategorizedCategory && productCount > 0 && <FilterSection value="ingredients" title="Ingredient" items={filterSummary?.ingredient} selectedItems={selectedIngredients} onItemChange={handleIngredientChange} skeletonCount={8} accordionValue={accordionValue} isLast={true} isLoading={!filterSummary} />}
          </>}
      </Accordion.Root>
    </Box>;
};
export default FilterSidebar;